import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import VSAIWidget from "@/components/VSAIWidget";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { Briefcase, ClipboardList, CheckCircle2, Clock, Award, Bell, User, ArrowRight, Sparkles, BookmarkCheck, Route, MapPin, Bot, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const KPI = ({ icon: Icon, label, value, color, testid }) => (
  <Card className="p-5 border-slate-200 card-hover" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
        <div className="font-display text-3xl font-bold text-[#0A2540] mt-1">{value}</div>
      </div>
      <div className={`p-3 rounded-lg ${color}`}><Icon className="w-5 h-5 text-white"/></div>
    </div>
  </Card>
);

export default function CandidateDashboard() {
  const [d, setD] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [actions, setActions] = useState({ completion: 0, actions: [] });
  const [recos, setRecos] = useState([]);
  const [saved, setSaved] = useState([]);
  const [roadmap, setRoadmap] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [prepOpen, setPrepOpen] = useState(null); // interview being prepped
  const [prepData, setPrepData] = useState(null);
  const [prepLoading, setPrepLoading] = useState(false);

  const openPrep = async (i) => {
    setPrepOpen(i); setPrepData(null); setPrepLoading(true);
    try {
      const { data } = await api.post("/candidate/interview-prep", { interview_id: i.id });
      setPrepData(data);
    } catch (err) { setPrepData({ error: "Preparation temporarily unavailable. Please try again shortly." }); }
    finally { setPrepLoading(false); }
  };

  useEffect(()=>{
    api.get("/candidate/dashboard").then(r=>setD(r.data));
    api.get("/notifications").then(r=>setNotifs(r.data));
    api.get("/candidate/profile-actions").then(r=>setActions(r.data));
    api.get("/candidate/recommendations").then(r=>setRecos(r.data));
    api.get("/saved-jobs").then(r=>setSaved(r.data));
    api.get("/candidate/roadmap").then(r=>setRoadmap(r.data));
    api.get("/candidate/interviews").then(r=>setInterviews(r.data)).catch(()=>{});
  },[]);

  if (!d) return <div><PublicHeader/><div className="p-10 text-slate-500">Loading...</div></div>;

  // determine "Your Next Action"
  const pendingAsmt = d.pending_assessments?.[0];
  let nextAction = null;
  if (pendingAsmt) nextAction = { title: `Complete your assessment for ${pendingAsmt.job_title}`, cta: "Start Assessment", link: `/assessment/${pendingAsmt.assessment_id}` };
  else if (actions.actions?.[0]) nextAction = { title: actions.actions[0].label, cta: "Complete Now", link: actions.actions[0].link };
  else nextAction = { title: "Explore matching opportunities", cta: "Browse Jobs", link: "/jobs" };

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0A2540]">Candidate Dashboard</h1>
            <p className="text-slate-500 mt-1">Your personalized career command center.</p>
          </div>
          <Button asChild variant="outline" data-testid="edit-profile-btn"><Link to="/candidate/profile"><User className="w-4 h-4 mr-2"/>Edit Profile</Link></Button>
        </div>

        {/* NEXT ACTION */}
        <Card className="p-6 border-slate-200 mb-6 bg-gradient-to-r from-[#0A2540] to-[#12203a] text-white" data-testid="next-action-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#F59E0B]/20 border border-[#F59E0B]/30 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#F59E0B]"/>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#F59E0B] font-semibold">Your Next Action</div>
                <div className="font-display text-xl font-semibold mt-1">{nextAction.title}</div>
              </div>
            </div>
            <Button asChild className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="next-action-cta">
              <Link to={nextAction.link}>{nextAction.cta} <ArrowRight className="w-4 h-4 ml-2"/></Link>
            </Button>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 mb-6" data-testid="profile-completion-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#2563EB] font-semibold">Profile Completion</div>
              <div className="mt-2 font-display text-2xl font-bold text-[#0A2540]">{actions.completion}%</div>
            </div>
            <div className="flex-1 min-w-64 max-w-2xl">
              <Progress value={actions.completion} className="h-2.5"/>
              <p className="text-sm text-slate-500 mt-2">A complete profile unlocks better recommendations and improves recruiter visibility.</p>
            </div>
          </div>
          {actions.actions?.length > 0 && (
            <div className="mt-4 grid md:grid-cols-2 gap-2">
              {actions.actions.slice(0,4).map(a => (
                <Link key={a.id} to={a.link} className="border border-slate-200 rounded-md px-3 py-2 flex items-center justify-between text-sm hover:border-[#2563EB] transition-colors" data-testid={`profile-action-${a.id}`}>
                  <span className="text-slate-700">{a.label}</span>
                  <span className="text-[#2563EB] font-semibold flex items-center gap-1 text-xs">Complete Now <ArrowRight className="w-3 h-3"/></span>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <KPI icon={Briefcase} label="Applications" value={d.kpis.applications} color="bg-[#0A2540]" testid="kpi-applications"/>
          <KPI icon={ClipboardList} label="Asmt Pending" value={d.kpis.assessments_pending} color="bg-[#F59E0B]" testid="kpi-asmt-pending"/>
          <KPI icon={CheckCircle2} label="Asmt Done" value={d.kpis.assessments_completed} color="bg-emerald-600" testid="kpi-asmt-done"/>
          <KPI icon={Clock} label="Under Review" value={d.kpis.under_review} color="bg-[#2563EB]" testid="kpi-under-review"/>
          <KPI icon={Award} label="Interviews" value={d.kpis.interviews} color="bg-purple-600" testid="kpi-interviews"/>
          <KPI icon={Award} label="Offers" value={d.kpis.offers} color="bg-pink-600" testid="kpi-offers"/>
        </div>

        <Tabs defaultValue="applications" className="mb-6">
          <TabsList>
            <TabsTrigger value="applications" data-testid="tab-applications">My Applications</TabsTrigger>
            <TabsTrigger value="interviews" data-testid="tab-interviews">Interviews</TabsTrigger>
            <TabsTrigger value="recommended" data-testid="tab-recommended">Recommended</TabsTrigger>
            <TabsTrigger value="saved" data-testid="tab-saved">Saved</TabsTrigger>
            <TabsTrigger value="roadmap" data-testid="tab-roadmap">Career Roadmap</TabsTrigger>
          </TabsList>

          <TabsContent value="applications">
            <Card className="p-6 border-slate-200" data-testid="applications-list">
              {d.recent_applications.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Briefcase className="w-8 h-8 mx-auto mb-2 text-slate-300"/>
                  <p>No applications yet.</p>
                  <Button asChild size="sm" className="mt-4 bg-[#0A2540] hover:bg-[#0A2540]/90"><Link to="/jobs">Browse Jobs</Link></Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {d.recent_applications.map(a=>(
                    <div key={a.id} className="border border-slate-200 rounded-lg p-4 flex justify-between items-center flex-wrap gap-3" data-testid={`app-row-${a.id}`}>
                      <div>
                        <div className="font-semibold text-[#0A2540]">{a.job_title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{a.id} · Applied {new Date(a.created_at).toLocaleDateString()}</div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Badge className="bg-slate-100 text-slate-700 border-none hover:bg-slate-100">{a.stage}</Badge>
                          {a.assessment_status === "completed" && <Badge className="bg-emerald-100 text-emerald-700 border-none hover:bg-emerald-100">Assessment {a.assessment_score}%</Badge>}
                          {a.assessment_status === "pending" && a.assessment_id && <Badge className="bg-amber-100 text-amber-800 border-none hover:bg-amber-100">Assessment Pending</Badge>}
                        </div>
                      </div>
                      {a.assessment_id && a.assessment_status !== "completed" && (
                        <Button asChild size="sm" className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90" data-testid={`continue-asmt-${a.id}`}>
                          <Link to={`/assessment/${a.assessment_id}`}>Continue Assessment <ArrowRight className="w-3 h-3 ml-1"/></Link>
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="interviews">
            <Card className="p-6 border-slate-200" data-testid="interviews-list">
              {interviews.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Award className="w-8 h-8 mx-auto mb-2 text-slate-300"/>
                  <p>No interviews scheduled yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {interviews.map(i => (
                    <div key={i.id} className={`border rounded-lg p-4 ${i.status === "cancelled" ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`} data-testid={`interview-${i.id}`}>
                      <div className="flex justify-between items-start flex-wrap gap-3">
                        <div>
                          <div className="font-semibold text-[#0A2540]">{i.job_title}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{i.round_name}</div>
                          <div className="text-sm text-slate-700 mt-2">{new Date(i.scheduled_at).toLocaleString()}{i.duration_min ? ` · ${i.duration_min}m` : ""}</div>
                        </div>
                        <Badge className={i.status === "cancelled" ? "bg-red-100 text-red-700 border-none" : i.status === "rescheduled" ? "bg-amber-100 text-amber-800 border-none" : "bg-emerald-100 text-emerald-700 border-none"}>{i.status}</Badge>
                      </div>
                      {i.meeting_link && i.status !== "cancelled" && (
                        <a href={i.meeting_link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#2563EB] hover:underline">
                          Join meeting <ArrowRight className="w-3 h-3"/>
                        </a>
                      )}
                      {i.status !== "cancelled" && (
                        <Button size="sm" variant="outline" onClick={()=>openPrep(i)} className="mt-3 ml-3 border-[#2563EB]/40 text-[#2563EB] hover:bg-blue-50" data-testid={`prep-ai-${i.id}`}>
                          <Bot className="w-4 h-4 mr-1"/>Prepare with VS AI
                        </Button>
                      )}
                      {i.instructions && <p className="text-xs text-slate-500 mt-2">{i.instructions}</p>}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="recommended">
            <Card className="p-6 border-slate-200">
              {recos.length === 0 ? <p className="text-sm text-slate-500 text-center py-8">Complete your profile with skills and experience to see matching opportunities.</p> :
                <div className="grid md:grid-cols-2 gap-3">
                  {recos.map(r => (
                    <Link key={r.job.id} to={`/jobs/${r.job.id}`} className="border border-slate-200 rounded-lg p-4 hover:border-[#2563EB] transition-colors flex justify-between gap-3" data-testid={`reco-${r.job.id}`}>
                      <div className="flex-1">
                        <div className="font-semibold text-[#0A2540]">{r.job.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5 flex gap-2"><MapPin className="w-3 h-3"/>{r.job.location} · {r.job.experience_category || `${r.job.experience_min}-${r.job.experience_max} yrs`}</div>
                        <div className="text-xs text-slate-600 mt-1">Matching: {r.matching_skills.slice(0,3).join(", ") || "—"}</div>
                      </div>
                      <div className="text-right">
                        <div className={`font-display text-2xl font-bold ${r.match>=75?"text-emerald-600":r.match>=50?"text-[#F59E0B]":"text-slate-400"}`}>{r.match}%</div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Match</div>
                      </div>
                    </Link>
                  ))}
                </div>
              }
            </Card>
          </TabsContent>

          <TabsContent value="saved">
            <Card className="p-6 border-slate-200">
              {saved.length === 0 ? <p className="text-sm text-slate-500 text-center py-8">No saved jobs yet. Bookmark opportunities you like to keep them here.</p> :
                <div className="grid md:grid-cols-2 gap-3">
                  {saved.map(j => (
                    <Link key={j.id} to={`/jobs/${j.id}`} className="border border-slate-200 rounded-lg p-4 hover:border-[#2563EB] transition-colors flex items-start gap-3" data-testid={`saved-${j.id}`}>
                      <BookmarkCheck className="w-4 h-4 text-[#F59E0B] mt-1"/>
                      <div className="flex-1">
                        <div className="font-semibold text-[#0A2540]">{j.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{j.location} · {j.experience_category || `${j.experience_min}-${j.experience_max} yrs`}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              }
            </Card>
          </TabsContent>

          <TabsContent value="roadmap">
            {roadmap && (
              <Card className="p-6 border-slate-200" data-testid="roadmap-card">
                <div className="text-xs uppercase tracking-[0.2em] text-[#2563EB] font-semibold">Suggested Career Path — {roadmap.domain}</div>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
                  {roadmap.path.map((step,i)=>(
                    <div key={step} className="relative">
                      <Card className="p-3 border-slate-200 text-center h-full">
                        <div className="w-7 h-7 rounded-full bg-[#0A2540] text-white flex items-center justify-center mx-auto font-display font-bold text-xs">{i+1}</div>
                        <div className="mt-2 text-xs font-semibold text-[#0A2540] leading-tight">{step}</div>
                      </Card>
                    </div>
                  ))}
                </div>
                <div className="mt-6 grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Recommended skills to grow</div>
                    <div className="flex flex-wrap gap-1.5">
                      {roadmap.recommended_skills.map(s => <Badge key={s} className="bg-blue-100 text-blue-800 border-none hover:bg-blue-100">{s}</Badge>)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Aligned jobs</div>
                    <div className="space-y-2">
                      {(roadmap.recommended_jobs || []).slice(0,3).map(j => (
                        <Link key={j.id} to={`/jobs/${j.id}`} className="block border border-slate-200 rounded-md p-2 text-sm hover:border-[#2563EB]">
                          <div className="font-semibold text-[#0A2540] text-sm">{j.title}</div>
                          <div className="text-xs text-slate-500">{j.location}</div>
                        </Link>
                      ))}
                      {(!roadmap.recommended_jobs || roadmap.recommended_jobs.length===0) && <p className="text-sm text-slate-500">No aligned jobs currently published.</p>}
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 mt-6">These are informational suggestions based on your current profile.</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        <Card className="p-6 border-slate-200 mt-6" data-testid="notifications-card">
          <div className="flex items-center gap-2 mb-4"><Bell className="w-4 h-4 text-[#2563EB]"/><h2 className="font-display font-semibold text-[#0A2540]">Notifications</h2></div>
          {notifs.length === 0 ? <p className="text-sm text-slate-500">No notifications yet.</p> : (
            <div className="grid md:grid-cols-2 gap-3">
              {notifs.slice(0,6).map(n=>(
                <div key={n.id} className={`text-sm p-3 rounded-md border ${n.read ? "bg-white border-slate-200" : "bg-blue-50 border-blue-200"}`} data-testid={`notif-${n.id}`}>
                  <div className="font-semibold text-[#0A2540]">{n.title}</div>
                  <div className="text-slate-600 mt-0.5">{n.body}</div>
                  <div className="text-[11px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
      <VSAIWidget/>
      <Dialog open={!!prepOpen} onOpenChange={(v)=>!v && (setPrepOpen(null), setPrepData(null))}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="prep-ai-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Bot className="w-5 h-5 text-[#2563EB]"/>Prepare with VS AI</DialogTitle></DialogHeader>
          {prepOpen && <>
            <p className="text-sm text-slate-500">{prepOpen.job_title} · {prepOpen.round_name}</p>
            {prepLoading && <div className="py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Analyzing job and generating your preparation brief…</div>}
            {prepData && prepData.error && <p className="text-sm text-red-600 mt-3">{prepData.error}</p>}
            {prepData && prepData.guidance && <div className="mt-4 space-y-5 text-sm">
              <Section title="Topics to revise" items={prepData.guidance.topics}/>
              <Section title="Key concepts" items={prepData.guidance.concepts}/>
              <Section title="Role-specific questions" items={prepData.guidance.role_questions}/>
              <Section title="Scenario / practical questions" items={prepData.guidance.scenario_questions}/>
              <Section title="Preparation checklist" items={prepData.guidance.checklist}/>
              <Section title="Skills to focus on" items={prepData.guidance.focus_skills}/>
              <Section title="Interview tips" items={prepData.guidance.tips}/>
              <p className="text-xs text-slate-500 italic">{prepData.disclaimer}</p>
            </div>}
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[#2563EB] font-semibold mb-2">{title}</div>
      <ul className="list-disc list-inside space-y-1 text-slate-700">
        {items.map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    </div>
  );
}
